import {Component, computed, inject} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {CreateQualityControlDialogData, LoadSelectDataParams} from "./types/create-quality-control.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {atLeastOneRequired, createQualityControlFormFields} from "./configs/create-quality-control-form-fields";
import {
  CustomMatSelectComponent
} from "../../../../../../shared/components/ui/custom-mat-select/custom-mat-select.component";
import {
  GetSelectDataParams,
  SelectData
} from "../../../../../../core/types/records-table.types";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {CreateQualityControlFormField} from "./types/create-quality-control-form-field.types";
import {
  needLoadSelectDataByField,
  qualityControlResourceType
} from "./configs/create-quality-control-config";
import {MatButton} from "@angular/material/button";
import {CreateQualityControlService} from "./services/create-quality-control.service";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-create-quality-control-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    CustomMatSelectComponent,
    ReactiveFormsModule,
    MatButton,
    LoaderComponent
  ],
  templateUrl: './create-quality-control-modal.component.html',
  styleUrl: './create-quality-control-modal.component.scss'
})
export class CreateQualityControlModalComponent extends BaseModalComponent {
  private createQualityControlDialogData = inject<CreateQualityControlDialogData>(MAT_DIALOG_DATA);
  private createQualityControlService: CreateQualityControlService = inject(CreateQualityControlService);

  public readonly qualityControlResourceType = qualityControlResourceType;
  public readonly createQualityControlFormFields = createQualityControlFormFields;
  public createQualityControlForm: FormGroup = new FormGroup({});
  public columnParams = computed(() => this.createQualityControlDialogData.columnsParams);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(qualityControlResourceType.key);
  }

  public getOptionsByKey(key: string): Array<SelectData> {
    return this.columnParams().find(i => i.key === key)?.metaSelectKeyList || [];
  }

  public handleSelectChange(value: string, key: string): void {
    this.createQualityControlForm.get(key)?.setValue(value);
  }

  public async handleCreateQualityControl(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params = {
      productCardId: this.createQualityControlDialogData.parentId,
      ...this.createQualityControlForm.value
    };

    await this.createQualityControlService.createQualityControlData(params)
      .then(() => this.closeDialog('success'))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async initModal(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.modalStatesService.openModal(qualityControlResourceType.key);
    this.createQualityControlForm = this.baseModalService.getFormGroup<CreateQualityControlFormField>(createQualityControlFormFields);
    this.createQualityControlForm.addValidators(atLeastOneRequired('nmId', 'sku'));
    this.createQualityControlForm.updateValueAndValidity();
    await Promise.all(
      needLoadSelectDataByField.map(item => this.loadSelectData(item))
    ).finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async loadSelectData({ key, propertyLazySelect }: LoadSelectDataParams): Promise<void> {
    const params: GetSelectDataParams = {
      propertyLazySelect: propertyLazySelect,
      id: this.createQualityControlDialogData.parentId
    };

    await this.createQualityControlService.getSelectData(params)
      .then(data => {
        const column = this.columnParams().find(i => i.key === key);
        if (column) {
          column.metaSelectKeyList = data;
        }
      });
  }
}
